import type { AttributionDateRange } from "./types"
import type { ComparisonMode } from "./period-constants"

/**
 * Compute the previous period date range with the same *elapsed* duration,
 * ending exactly where the current period starts.
 *
 * When the current period extends into the future (e.g. "This Month" viewed
 * mid-month), the duration is clamped to the elapsed portion so that we
 * compare like-for-like instead of 20 days of data vs a full 28-day period.
 *
 * @param dateRange  The current date range
 * @param now        Override for "now" (for testing). Defaults to `new Date()`.
 *
 * @example
 *   // Full period (last month, already complete)
 *   current  = Jan 1 – Feb 1  (31 days)
 *   previous = Dec 1 – Jan 1  (31 days)
 *
 * @example
 *   // Partial period ("this month" on Feb 20)
 *   current  = Feb 1 – Mar 1  (nominal 28 days, but only 20 elapsed)
 *   previous = Jan 12 – Feb 1 (20 days — matches elapsed, not nominal)
 */
export function getPreviousPeriodRange(
  dateRange: AttributionDateRange,
  now?: Date,
): AttributionDateRange {
  const effectiveNow = now ?? new Date()

  // Clamp end to "now" so we only count elapsed time when the range
  // extends into the future (e.g. "this_month" mid-month).
  const clampedEnd = new Date(
    Math.min(dateRange.month_end_utc.getTime(), effectiveNow.getTime()),
  )

  // Guard: if clamped end is at or before start (clock skew, etc.),
  // fall back to the full nominal duration.
  const elapsedMs = clampedEnd.getTime() - dateRange.month_start_utc.getTime()
  const durationMs = elapsedMs > 0
    ? elapsedMs
    : dateRange.month_end_utc.getTime() - dateRange.month_start_utc.getTime()

  return {
    month_start_utc: new Date(dateRange.month_start_utc.getTime() - durationMs),
    month_end_utc: new Date(dateRange.month_start_utc.getTime()),
    timezone: dateRange.timezone,
  }
}

/**
 * Shift a date range back by exactly one year.
 * Handles Feb 29 → Feb 28 when landing in a non-leap year.
 */
export function getSamePeriodLastYearRange(
  dateRange: AttributionDateRange,
): AttributionDateRange {
  const shiftByYear = (d: Date): Date => {
    const shifted = new Date(d.getTime())
    shifted.setUTCFullYear(shifted.getUTCFullYear() - 1)
    // If the original date was e.g. Feb 29 but the target year has no Feb 29,
    // JS rolls it forward to Mar 1. Clamp back to Feb 28.
    if (shifted.getUTCMonth() !== d.getUTCMonth()) {
      shifted.setUTCDate(0) // last day of the previous month
    }
    return shifted
  }

  return {
    month_start_utc: shiftByYear(dateRange.month_start_utc),
    month_end_utc: shiftByYear(dateRange.month_end_utc),
    timezone: dateRange.timezone,
  }
}

/**
 * Resolve a ComparisonMode into a concrete date range (or null for "none").
 */
export function resolveComparisonRange(
  mode: ComparisonMode,
  dateRange: AttributionDateRange,
  customRange?: AttributionDateRange | null,
  now?: Date,
): AttributionDateRange | null {
  switch (mode) {
    case "none":
      return null
    case "previous_period":
      return getPreviousPeriodRange(dateRange, now)
    case "same_period_last_year":
      return getSamePeriodLastYearRange(dateRange)
    case "custom":
      return customRange ?? null
  }
}
